/* eslint-disable no-shadow */

import { loadAsyncComponent, Logger } from '@fifa-bsm/bo-frontend-shared/core';
import { eventsApi } from '@/app/shared/services/apis/eventsApi.service';
import { seatingApi } from '@/app/shared/services/apis/seatingApi.service';
import { uniqueValues } from '@fifa-bsm/bo-frontend-shared/utils';
import { guestOperationsSeatAllocationApi } from '@/app/shared/services/apis/guestOperationsSeatAllocationApi.service';
import { GOSeatCategoryType } from '@/app/modules/seating/guestOperations/Picker/domain/enums/GOSeatCategoryType';
import { eventGOSeatCategoriesApi } from '@/app/shared/services/apis/eventGOSeatCategoriesApi.service';
import { trimSeatPlanObjectsCoords } from '@/app/modules/seating/shared/domain/seatAllocator/canvas/utils/trimSeatPlanObjectsCoords';
import { ticketsDistributionApi } from '@/app/shared/services/apis/ticketsDistributionApi.service';
import {
    isCancellingTicket,
    isTicketDistributed,
} from '@/app/modules/seating/guestOperations/Picker/domain/tickets/tickets';
import { partition } from '@fifa-bsm/bo-frontend-shared/utils';
import { BACKDROP_EVENTS } from '@fifa-bsm/bo-frontend-shared/components/AppBackdrop/backdropEvents';
import { UserEntityPermission } from '@/app/shared/enums/UserEntityPermission';
import { GuestsVisibility } from '@/app/modules/seating/guestOperations/Picker/domain/enums/GuestsVisibility';
import { ETicketingType } from '@/app/shared/enums/ETicketingType';
import { participantIdentifiersApi } from '@/app/shared/services/apis/participantIdentifiersApi.service';
import { getSeatCategoriesStatistics } from '@/app/store/seatAllocation/shared/domain/getSeatCategoriesStatistics';
import { localeComparator, sortByMany, uniqueBy } from '@fifa-bsm/bo-frontend-shared/utils';
import { SortDirection } from '@/app/shared/constants/sortDirection';
import { mainEventsApi } from '@/app/shared/services/apis/mainEventsApi.service';
import { isModalCancelled } from '@fifa-bsm/bo-frontend-shared/components/AppBackdrop/ModalCancelled';
import { dispatchShowBackdropComponent } from '@fifa-bsm/bo-frontend-shared/components/AppBackdrop/showBackdropComponent';
import { isEmpty } from '@fifa-bsm/bo-frontend-shared/validators';

const initialState = () => ({
    loadingPage: false,
    loadingPageError: null,
    loading: false,
    seatPlan: null,
    mainEvent: null,
    event: null,
    seatingSettings: null,
    seatCategories: [],
    participantIdentifiers: [],
    goSeatCategoriesByType: {
        global_seat_categories: [],
        main_event_seat_categories: [],
    },
    selectedSeat: null,
    previewedSeat: null,
    GOSeatPlans: {
        available: [],
        current: null,
    },
    batch: {
        enabled: false,
        tickets: [],
        currentIndex: null,
        currentTicketId: null,
    },
    isTabletMode: false,
    highlightedSeatIdsWithoutAllocation: [],
    seatIdToPDFTemplateMap: {},
    processedSeatIds: [],
    seatIdsToReRender: [],
    reRenderId: 0,
    usingFabricJs: false,
});

const getters = {
    mainEventId(state) {
        return state.event?.main_event.id;
    },
    eventId(state) {
        return state.event?.id;
    },
    venueId(state) {
        return state.event?.venue?.id;
    },
    eTicketingType(state) {
        return state.mainEvent?.eticketing_type;
    },
    isETicketingEnabled(state, getters) {
        return getters.eTicketingType !== ETicketingType.DISABLED;
    },
    isStandardETicketingEnabled(state, getters) {
        return getters.eTicketingType === ETicketingType.STANDARD;
    },
    isSecutixETicketingEnabled(state, getters) {
        return getters.eTicketingType === ETicketingType.SECUTIX;
    },
    isFortressEnabledForETicketing(state) {
        return state.mainEvent?.eticketing_with_fortress ?? false;
    },
    allGOSeatCategories(state) {
        return [
            ...state.goSeatCategoriesByType.global_seat_categories.map((seatCategory) => ({
                ...seatCategory,
                type: GOSeatCategoryType.GLOBAL,
            })),
            ...state.goSeatCategoriesByType.main_event_seat_categories.map((seatCategory) => ({
                ...seatCategory,
                type: GOSeatCategoryType.MAIN_EVENT,
            })),
        ];
    },
    computedSeatPlan(state, getters, rootState) {
        if (!state.seatPlan) {
            return null;
        }

        const selectedAreaId = rootState.SeatAllocation.guestOperations.PickerTopBar.selectedAreaId;
        const selectedSeatCategoryId =
            rootState.SeatAllocation.guestOperations.PickerTopBar.selectedSeatCategoryId;

        const selectedAreaFilter = (seat, block) => {
            if (!selectedAreaId) {
                return true;
            }

            let filterValue = selectedAreaId.split(':')[1];

            if (selectedAreaId.startsWith('seatGroups:')) {
                filterValue = parseInt(filterValue, 10);
                return seat.category?.seat_group?.id === filterValue;
            }

            if (selectedAreaId.startsWith('blockLevels:')) {
                return block.level === filterValue;
            }

            return true;
        };

        const seatCategoryFilter = (seat) => {
            if (selectedSeatCategoryId) {
                return seat.category?.id === selectedSeatCategoryId;
            }

            return true;
        };

        const isSeatVisible = (seat, block) =>
            selectedAreaFilter(seat, block) && seatCategoryFilter(seat);

        const seatPlan = {
            ...state.seatPlan,
            blocks: state.seatPlan.blocks.map((block) => ({
                ...block,
                rows: block.rows.map((row) => ({
                    ...row,
                    seats: row.seats.map((seat) => {
                        const goSeatCategory = getters.allGOSeatCategories.find(
                            (category) => category.id === seat.guest_operations_seat_category
                        );
                        const hasAccessByEntity = getters.seatCategoriesAccessibleByEntity
                            ? goSeatCategory?.id
                                ? getters.seatCategoriesAccessibleByEntity.some(
                                      (entityGoSeatCategory) =>
                                          entityGoSeatCategory.id === goSeatCategory.id
                                  )
                                : false
                            : true;

                        return {
                            ...seat,
                            // resetting rows and seats to avoid recursion (breaks vue dev tools)
                            block: { ...block, rows: undefined },
                            row: { ...row, seats: undefined },
                            goSeatCategory,
                            isVisible: isSeatVisible(seat, block),
                            hasAccessByEntity,
                        };
                    }),
                })),
            })),
        };

        return trimSeatPlanObjectsCoords(seatPlan);
    },
    allSeatPlanSeats(state) {
        return (
            state.seatPlan?.blocks.flatMap((block) => block.rows.flatMap((row) => row.seats)) ?? []
        );
    },
    allComputedSeats(state, getters) {
        if (!getters.computedSeatPlan) {
            return [];
        }

        return getters.computedSeatPlan.blocks.flatMap((block) =>
            block.rows.flatMap((row) => row.seats)
        );
    },
    seatsMap(state, getters) {
        return getters.allComputedSeats.reduce((seatsMap, seat) => {
            seatsMap[seat.id] = seat;
            return seatsMap;
        }, {});
    },
    categoriesInSeatPlan(state, getters, rootState, rootGetters) {
        if (!state.seatPlan) {
            return [];
        }

        const userGroupCode = rootGetters['User/userGroupCode'];
        const seatCategoriesForCurrentUser = uniqueValues(
            getters.allComputedSeats
                .filter((seat) => seat.category?.seat_group?.type === userGroupCode)
                .map((seat) => seat.category.id)
        );

        const allocatedSeatIds = new Set(
            rootGetters['SeatAllocation/guestOperations/TicketList/tickets']
                .map((ticket) => ticket.seat?.id)
                .filter(Boolean)
        );

        const allocationStatistics = getSeatCategoriesStatistics(
            getters.allSeatPlanSeats,
            allocatedSeatIds
        );

        return state.seatCategories
            .filter((category) => seatCategoriesForCurrentUser.includes(category.id))
            .map((category) => ({
                ...category,
                allocated: allocationStatistics[category.id]?.allocated ?? 0,
                free: allocationStatistics[category.id]?.free ?? 0,
                capacity: allocationStatistics[category.id]?.capacity ?? 0,
            }));
    },
    isBatchEnabled(state) {
        return state.batch.enabled;
    },
    availableGOSeatPlans(state) {
        return state.GOSeatPlans.available.map((seatPlan, index) => ({
            ...seatPlan,
            number: index + 1,
        }));
    },
    currentGOSeatPlan(state) {
        return state.GOSeatPlans.current;
    },
    currentGOSeatPlanId(state, getters) {
        return getters.currentGOSeatPlan?.id;
    },
    isCurrentGOSeatPlanOfficial(state) {
        return state.GOSeatPlans.current?.is_official ?? false;
    },
    canAssignSeatsInBulk(state, getters, rootState, rootGetters) {
        return (
            state.highlightedSeatIdsWithoutAllocation.length >=
            rootGetters['SeatAllocation/guestOperations/TicketList/notProcessedActiveTickets']
                .length
        );
    },
    userEntity(state) {
        return state.mainEvent?.user_entity || null;
    },
    seatCategoriesAccessibleByEntity(state, getters, rootState) {
        if (!getters.userEntity) {
            return null;
        }

        if (
            getters.userEntity.permission === UserEntityPermission.LIMITED_ACCESS ||
            rootState.SeatAllocation.guestOperations.TicketList.guestsVisibility ===
                GuestsVisibility.LIMITED_BY_ENTITY
        ) {
            return getters.userEntity.go_seat_categories;
        }

        return null;
    },
    processedSeatIdsMap(state) {
        const map = {};
        for (const id of state.processedSeatIds) {
            map[id] = true;
        }

        return map;
    },
    shouldShowPitchLine(state) {
        return state.seatingSettings?.go_show_pitch_line_in_seat_allocator ?? false;
    },
    allProcessedSeatIds(state, getters, rootState, rootGetters) {
        return [
            ...state.processedSeatIds,
            ...rootGetters['SeatAllocation/guestOperations/TicketList/tickets']
                .filter(isCancellingTicket)
                .map((ticket) => ticket.venue_seat_id)
                .filter(Boolean),
        ];
    },
};

const actions = {
    async loadSeatPickerData(
        { state, commit, dispatch },
        { mainEventId, eventId, withLoading = true, setDefaultGOSeatPlan = true }
    ) {
        if (withLoading) {
            commit('setLoadingPage', true);
        }

        commit('setLoadingPageError', null);
        try {
            const [mainEvent, event, seatingSettings, seatPlans] = await Promise.all([
                mainEventsApi
                    .getById(mainEventId, { extended: true })
                    .then((response) => response.data),
                eventsApi.getById(mainEventId, eventId).then((response) => response.data),
                eventsApi
                    .getSeatingSettings(mainEventId, eventId)
                    .then((response) => response.data),
                guestOperationsSeatAllocationApi
                    .getSeatPlans(eventId)
                    .then((response) => response.data),
                dispatch('loadSeatCategories', { mainEventId }),
                dispatch('loadParticipantIdentifiers', { mainEventId }),
                dispatch('loadPDFTemplatesAssignedToSeat', { mainEventId, eventId }),
            ]);

            commit('setGOSeatPlans', seatPlans);
            commit('setMainEvent', mainEvent);
            commit('setEvent', event);
            commit('setSeatingSettings', seatingSettings);

            if (setDefaultGOSeatPlan) {
                const officialSeatPlan = seatPlans.find((seatPlan) => seatPlan.is_official);
                const seatTagPlan = seatPlans.find((seatPlan) => seatPlan.is_seat_tag_plan);
                commit(
                    'setCurrentGOSeatPlan',
                    state.isTabletMode ? (seatTagPlan ?? officialSeatPlan) : officialSeatPlan
                );
            }

            const seatPlanId = event.booking_configuration.tribune_seat_plan_id;

            await dispatch('fetchSeatPlanAndTicketList', {
                eventId: event.id,
                seatPlanId,
                GOSeatPlanId: state.GOSeatPlans.current.id,
            });
        } finally {
            commit('setLoadingPage', false);
        }
    },
    async refreshSeatPickerData(
        { getters, dispatch },
        { withLoading = false, setDefaultGOSeatPlan = false } = {}
    ) {
        await dispatch('loadSeatPickerData', {
            mainEventId: getters.mainEventId,
            eventId: getters.eventId,
            withLoading,
            setDefaultGOSeatPlan,
        });
    },
    async loadPDFTemplatesAssignedToSeat(
        { commit },
        { mainEventId, eventId, withLoading = false }
    ) {
        if (withLoading) {
            commit('setLoading', true);
        }

        try {
            const data = (await seatingApi.getPDFTemplatesAssignedToSeats(mainEventId, eventId))
                .data;

            const seatIdToPDFTemplateMap = data.pdf_templates.reduce((map, templateDetails) => {
                const template = { id: templateDetails.id, name: templateDetails.name };
                templateDetails.seats.forEach((seatId) => {
                    map[seatId] = template;
                });
                return map;
            }, {});

            commit('setSeatIdToPDFTemplateMap', seatIdToPDFTemplateMap);
        } finally {
            commit('setLoading', false);
        }
    },
    async refreshPDFTemplatesAssignedToSeat({ getters, dispatch }, { withLoading = false } = {}) {
        await dispatch('loadPDFTemplatesAssignedToSeat', {
            mainEventId: getters.mainEventId,
            eventId: getters.eventId,
            withLoading,
        });
    },
    async loadSeatCategories({ commit }, { mainEventId }) {
        const [seatCategories, GOSeatCategories] = await Promise.all([
            seatingApi.getSeatCategories().then((response) => response.data),
            eventGOSeatCategoriesApi.getCategories(mainEventId).then((response) => response.data),
        ]);
        commit('setSeatCategories', seatCategories);
        commit('setGOSeatCategoriesByType', GOSeatCategories);
    },
    async loadParticipantIdentifiers({ commit }, { mainEventId }) {
        const participantIdentifiers = await participantIdentifiersApi
            .getList(mainEventId)
            .then((response) => response.data);
        commit('setParticipantIdentifiers', participantIdentifiers);
    },
    async loadSeatPlan({ state, getters, commit }, { seatPlanId, GOSeatPlanId } = {}) {
        const seatPlan = (
            await seatingApi.getGOAllocationSeatPlan(
                state.event.id,
                seatPlanId ?? state.seatPlan.id,
                GOSeatPlanId ?? getters.currentGOSeatPlan.id
            )
        ).data;

        Object.freeze(seatPlan);

        commit('setSeatPlan', seatPlan);
    },
    async refreshSeatPlan({ state, getters, dispatch }) {
        if (!state.seatPlan || !getters.currentGOSeatPlan) {
            Logger.warn('No seat plan or GO seat plan available');
            return;
        }

        await dispatch('loadSeatPlan', {
            seatPlanId: state.seatPlan.id,
            GOSeatPlanId: getters.currentGOSeatPlan.id,
        });
    },
    async refreshTicketsList(
        { getters, commit, dispatch },
        {
            withLoading = false,
            ticketIds,
            participantIds,
            deselectTickets = true,
            seatIdsToReRender,
        } = {}
    ) {
        if (!getters.eventId || !getters.currentGOSeatPlanId) {
            return;
        }

        if (
            (isEmpty(ticketIds) || ticketIds.length === 0) &&
            (isEmpty(participantIds) || participantIds.length === 0)
        ) {
            Logger.warn('No tickets or participants provided. Verify the call.', new Error().stack);
        }

        if (withLoading) {
            commit('setLoading', true);
        }
        try {
            if (ticketIds || participantIds) {
                if (ticketIds && deselectTickets) {
                    await commit(
                        'SeatAllocation/guestOperations/TicketList/removeManyTicketsFromActiveForAllocationList',
                        ticketIds,
                        { root: true }
                    );
                }

                await dispatch(
                    'SeatAllocation/guestOperations/TicketList/fetchParticipantsByIdsAndUpdateItems',
                    { ticketIds, participantIds },
                    { root: true }
                );
            } else {
                await dispatch(
                    'SeatAllocation/guestOperations/TicketList/fetchList',
                    { eventId: getters.eventId, GOSeatPlanId: getters.currentGOSeatPlanId },
                    { root: true }
                );
            }
        } finally {
            if (seatIdsToReRender) {
                commit('addSeatIdsToReRender', seatIdsToReRender);
            }
            commit('setLoading', false);
        }
    },
    async fetchSeatPlanAndTicketList({ commit, dispatch }, { eventId, seatPlanId, GOSeatPlanId }) {
        await Promise.all([
            dispatch('loadSeatPlan', { seatPlanId, GOSeatPlanId }),
            dispatch(
                'SeatAllocation/guestOperations/TicketList/fetchList',
                { eventId, GOSeatPlanId },
                { root: true }
            ),
        ]);

        commit('bumpReRenderId');
    },
    async refreshSeatPlanAndTicketList(
        { state, getters, commit, dispatch },
        { withLoading } = { withLoading: false }
    ) {
        if (withLoading) {
            commit('setLoading', true);
        }
        try {
            await dispatch('fetchSeatPlanAndTicketList', {
                eventId: getters.eventId,
                seatPlanId: state.seatPlan.id,
                GOSeatPlanId: getters.currentGOSeatPlan.id,
            });
        } finally {
            if (withLoading) {
                commit('setLoading', false);
            }
        }
    },
    async allocate({ commit, getters, dispatch }, { seatId, ticket, withLoading = false }) {
        if (withLoading) {
            commit('setLoading', true);
        }

        const seatIds = [seatId, ticket.seat?.id];

        try {
            dispatch('addToProcessed', { seatIds, ticketIds: [ticket.id] });

            await guestOperationsSeatAllocationApi.allocateSeats(
                getters.eventId,
                getters.currentGOSeatPlan.id,
                [
                    {
                        ticket_id: ticket.id,
                        venue_seat_id: seatId,
                    },
                ],
                { showErrorToast: false }
            );

            await dispatch('refreshTicketsList', { ticketIds: [ticket.id] });
        } finally {
            dispatch('removeFromProcessed', { seatIds, ticketIds: [ticket.id] });
            commit('setLoading', false);
        }
    },
    async swapSeats(
        { getters, commit, dispatch },
        { firstTicket, secondTicket, withLoading = false }
    ) {
        if (withLoading) {
            commit('setLoading', true);
        }

        const seatIds = [firstTicket.seat.id, secondTicket.seat.id];
        const ticketIds = [firstTicket.id, secondTicket.id];

        try {
            dispatch('addToProcessed', { seatIds, ticketIds });
            await guestOperationsSeatAllocationApi.swapSeats(
                getters.eventId,
                getters.currentGOSeatPlan.id,
                {
                    tickets: [firstTicket.id, secondTicket.id],
                }
            );
            await dispatch('refreshTicketsList', { ticketIds: [firstTicket.id, secondTicket.id] });
        } finally {
            dispatch('removeFromProcessed', { seatIds, ticketIds });
            commit('setLoading', false);
        }
    },
    async flashSeatTags({ getters }, { ticketIds }) {
        await guestOperationsSeatAllocationApi.flashSeatTags(
            getters.eventId,
            getters.currentGOSeatPlan.id,
            {
                ticket_ids: ticketIds,
            }
        );
    },
    async generateTicketAndAllocateSeat({ getters, commit, dispatch }, { guestId, seatId }) {
        commit('setLoading', true);

        try {
            dispatch('addToProcessed', { seatIds: [seatId] });
            await guestOperationsSeatAllocationApi.generateTicketAndAllocateSeat(
                getters.eventId,
                getters.currentGOSeatPlan.id,
                {
                    guest_id: guestId,
                    seat_id: seatId,
                }
            );
            await dispatch('refreshTicketsList');
        } finally {
            dispatch('removeFromProcessed', { seatIds: [seatId] });
            commit('setLoading', false);
        }
    },
    async removeAllocation(
        { getters, commit, dispatch, rootGetters },
        { ticket, undoDistribution, lastMinuteChanges, withLoading = false }
    ) {
        if (withLoading) {
            commit('setLoading', true);
        }

        let additionalTicketsToRefresh = [];
        if (ticket.mismatch) {
            additionalTicketsToRefresh = findRelatedTickets(
                rootGetters['SeatAllocation/guestOperations/TicketList/ticketsTree'],
                ticket
            );
        }

        dispatch('addToProcessed', { seatIds: [ticket.seat.id], ticketIds: [ticket.id] });

        try {
            await guestOperationsSeatAllocationApi.removeAllocation(
                getters.eventId,
                getters.currentGOSeatPlan.id,
                { ticketId: ticket.id, undoDistribution, lastMinuteChanges }
            );
            commit('setHighlightedSeatIdsWithoutAllocation', []);
            await dispatch('refreshTicketsList', {
                ticketIds: [ticket.id, ...additionalTicketsToRefresh.map((_ticket) => _ticket.id)],
                seatIdsToReRender: additionalTicketsToRefresh
                    .map((_ticket) => _ticket.seat?.id)
                    .filter(Boolean),
            });
        } finally {
            dispatch('removeFromProcessed', { seatIds: [ticket.seat.id], ticketIds: [ticket.id] });
            commit('setLoading', false);
        }
    },
    async removeAllocationFromMultipleSeats(
        { getters, commit, dispatch, rootGetters },
        { tickets, undoDistribution, lastMinuteChanges, withLoading = false }
    ) {
        if (withLoading) {
            commit('setLoading', true);
        }

        const ticketIds = tickets.map((ticket) => ticket.id);
        const seatIds = tickets.map((ticket) => ticket.seat?.id);

        const ticketsWithMismatch = tickets.filter((ticket) => ticket.mismatch);
        const additionalTicketsToRefresh = uniqueBy(
            (ticket) => ticket.id,
            ticketsWithMismatch.map((ticketWithMismatch) =>
                findRelatedTickets(
                    rootGetters['SeatAllocation/guestOperations/TicketList/ticketsTree'],
                    ticketWithMismatch
                ).flat()
            )
        );

        dispatch('addToProcessed', { seatIds, ticketIds });

        try {
            if (undoDistribution && getters.eTicketingType === ETicketingType.STANDARD) {
                const distributedTicketIds = ticketIds.filter((ticketId) =>
                    isTicketDistributed(
                        rootGetters['SeatAllocation/guestOperations/TicketList/ticketsMap'][
                            ticketId
                        ]
                    )
                );

                if (distributedTicketIds.length > 0) {
                    await ticketsDistributionApi.cancelETicketsDistribution(
                        getters.mainEventId,
                        getters.eventId,
                        {
                            tickets: distributedTicketIds.map((ticketId) => ({
                                id: ticketId,
                                emails_to: [],
                                emails_cc: [],
                                emails_bcc: [],
                            })),
                        }
                    );
                }

                await guestOperationsSeatAllocationApi.deallocateMultipleSeats(
                    getters.eventId,
                    getters.currentGOSeatPlanId,
                    { ticketIds, undoDistribution: false, lastMinuteChanges }
                );
            } else {
                await guestOperationsSeatAllocationApi.deallocateMultipleSeats(
                    getters.eventId,
                    getters.currentGOSeatPlanId,
                    { ticketIds, undoDistribution, lastMinuteChanges }
                );
            }

            commit('SeatAllocation/guestOperations/TicketList/setTicketsActiveForAllocation', [], {
                root: true,
            });
            await dispatch(
                'SeatAllocation/guestOperations/TicketList/fetchParticipantsByIdsAndUpdateItems',
                {
                    ticketIds: [
                        ...ticketIds,
                        ...additionalTicketsToRefresh.map((ticket) => ticket.id),
                    ],
                },
                {
                    root: true,
                }
            );

            if (additionalTicketsToRefresh.length > 0) {
                commit(
                    'addSeatIdsToReRender',
                    additionalTicketsToRefresh.map((ticket) => ticket?.seat?.id).filter(Boolean)
                );
            }
        } finally {
            dispatch('removeFromProcessed', { seatIds, ticketIds });
            commit('setLoading', false);
        }
    },
    async changeSeatAllocation(
        { getters, commit, dispatch },
        { seatId, ticketToDeallocate, ticketToAllocate, withLoading = false }
    ) {
        if (withLoading) {
            commit('setLoading', true);
        }

        const ticketIds = [ticketToAllocate.id, ticketToDeallocate.id];
        const seatIds = [seatId, ticketToDeallocate.seat?.id, ticketToAllocate.seat?.id];

        dispatch('addToProcessed', { ticketIds, seatIds });

        try {
            await guestOperationsSeatAllocationApi.removeAllocation(
                getters.eventId,
                getters.currentGOSeatPlan.id,
                { ticketId: ticketToDeallocate.id }
            );
            await guestOperationsSeatAllocationApi.allocateSeats(
                getters.eventId,
                getters.currentGOSeatPlan.id,
                [
                    {
                        ticket_id: ticketToAllocate.id,
                        venue_seat_id: seatId,
                    },
                ]
            );
            await dispatch('refreshTicketsList', {
                ticketIds: [ticketToAllocate.id, ticketToDeallocate.id],
            });
        } finally {
            dispatch('removeFromProcessed', { ticketIds, seatIds });
            commit('setLoading', false);
        }
    },
    async setSeatLocked({ state, commit, dispatch }, { seatId, locked }) {
        commit('setLoading', true);
        dispatch('addToProcessed', { seatIds: [seatId] });
        try {
            await (locked
                ? seatingApi.lockSeat(state.event.id, seatId)
                : seatingApi.unlockSeat(state.event.id, seatId));
            await dispatch('refreshSeatPlan');
        } finally {
            dispatch('removeFromProcessed', { seatIds: [seatId] });
            commit('setLoading', false);
        }
    },
    async updateSeatCategory(
        { getters, commit, dispatch },
        { seatIds, updateType, categoryId, withLoading = false }
    ) {
        if (withLoading) {
            commit('setLoading', true);
        }

        dispatch('addToProcessed', { seatIds });

        try {
            await eventGOSeatCategoriesApi.setForSeats(getters.eventId, {
                go_seat_plan_id: getters.currentGOSeatPlan.id,
                type: updateType,
                seats: seatIds.map((seatId) => ({
                    venue_seat_id: seatId,
                    go_seat_category_id: categoryId,
                })),
            });

            await dispatch('refreshSeatPlan');
        } finally {
            dispatch('removeFromProcessed', { seatIds });
            commit('setLoading', false);
        }
    },
    async updateProtocolHeart(
        { getters, commit, dispatch },
        { seatIds, enabled, updateType, withLoading = false }
    ) {
        if (withLoading) {
            commit('setLoading', true);
        }

        dispatch('addToProcessed', { seatIds });

        try {
            await guestOperationsSeatAllocationApi.setProtocolHeart(
                getters.mainEventId,
                getters.eventId,
                {
                    seats: seatIds,
                    is_protocol_heart: enabled,
                    go_seat_plan_id: getters.currentGOSeatPlan.id,
                    type: updateType,
                }
            );

            await dispatch('refreshSeatPlan');
        } finally {
            dispatch('removeFromProcessed', { seatIds });
            commit('setLoading', false);
        }
    },
    async updateParticipantIdentifier(
        { getters, commit, dispatch },
        { tickets, participantIdentifierId, withLoading = false }
    ) {
        if (withLoading) {
            commit('setLoading', true);
        }

        const seatIds = tickets.map((ticket) => ticket.seat?.id).filter(Boolean);
        const guestIds = uniqueValues(tickets.map((ticket) => ticket.guest.id).filter(Boolean));

        dispatch('addToProcessed', { seatIds });

        try {
            await participantIdentifiersApi.setForGuests(getters.mainEventId, {
                assignments: guestIds.map((guestId) => ({
                    go_individual_id: guestId,
                    go_participant_identifier_id: participantIdentifierId,
                })),
            });

            await dispatch('refreshTicketsList', {
                ticketIds: tickets.map((ticket) => ticket.id),
            });
        } finally {
            dispatch('removeFromProcessed', { seatIds });
            commit('setLoading', false);
        }
    },
    endBatchAssignment({ commit }) {
        commit('SeatAllocation/guestOperations/TicketList/setTicketsActiveForAllocation', [], {
            root: true,
        });
    },
    async refreshGOSeatPlans({ state, commit }) {
        const seatPlans = await guestOperationsSeatAllocationApi
            .getSeatPlans(state.event.id)
            .then((response) => response.data);
        const activeSeatPlan =
            seatPlans.find((plan) => plan.id === state.GOSeatPlans.current.id) ??
            seatPlans.find((plan) => plan.is_official);
        commit('setGOSeatPlans', seatPlans);
        commit('setCurrentGOSeatPlan', activeSeatPlan);
    },
    async createNewGOSeatPlan({ state, dispatch }) {
        await guestOperationsSeatAllocationApi.createSeatPlan(state.event.id);
        await dispatch('refreshGOSeatPlans');
    },
    async removeGOSeatPlan({ state, getters, dispatch }, seatPlan) {
        const selectedSeatPlanId = getters.currentGOSeatPlan.id;
        await guestOperationsSeatAllocationApi.removeSeatPlan(state.event.id, seatPlan.id);
        await dispatch('refreshGOSeatPlans');
        if (getters.currentGOSeatPlan.id !== selectedSeatPlanId) {
            await dispatch('refreshSeatPlanAndTicketList');
        }
    },
    async changePrimarySeatPlan({ state, getters, dispatch }, seatPlan) {
        await guestOperationsSeatAllocationApi.changePrimarySeatPlan(state.event.id, seatPlan.id);
        await dispatch('refreshGOSeatPlans');
        await dispatch(
            'changeCurrentSeatPlan',
            getters.availableGOSeatPlans.find((plan) => plan.id === seatPlan.id)
        );
    },
    async changeCurrentSeatPlan({ getters, commit, dispatch }, seatPlan) {
        commit('setCurrentGOSeatPlan', seatPlan);
        commit('SeatAllocation/guestOperations/TicketList/setTicketsActiveForAllocation', [], {
            root: true,
        });
        if (getters.isBatchEnabled) {
            dispatch('endBatchAssignment');
        }
        await dispatch('refreshSeatPlanAndTicketList', { withLoading: true });
    },
    async changeSeatTagSeatPlan({ getters, dispatch, commit }, seatPlanId) {
        await seatingApi.markSeatPlanForSeatTags(getters.eventId, seatPlanId);
        await dispatch('refreshGOSeatPlans');
        commit('setSeatTagPlan', seatPlanId);
    },
    async importSeatAllocationFromPrimaryPlan({ getters, dispatch }, { targetSeatPlanId }) {
        await guestOperationsSeatAllocationApi.importAllocationFromPrimaryPlan(
            getters.eventId,
            targetSeatPlanId
        );
        await dispatch('refreshGOSeatPlans');
        if (targetSeatPlanId === getters.currentGOSeatPlan.id) {
            await dispatch('refreshSeatPlanAndTicketList', { withLoading: true });
        }
    },
    async distributeTickets({ getters, dispatch }, { eventId, tickets }) {
        const seatIds = tickets.map((ticket) => ticket.seat.id);
        const ticketIds = tickets.map((ticket) => ticket.id);

        dispatch('addToProcessed', { seatIds, ticketIds });

        try {
            if (getters.isSecutixETicketingEnabled) {
                await ticketsDistributionApi
                    .forSecutix()
                    .distributePhysicalTickets(getters.mainEventId, eventId, {
                        tickets: ticketIds,
                    });
            } else {
                await ticketsDistributionApi.distributeTickets(
                    getters.mainEventId,
                    eventId,
                    ticketIds
                );
            }
            await dispatch('refreshTicketsList', { withLoading: false, ticketIds });
        } finally {
            dispatch('removeFromProcessed', { seatIds, ticketIds });
        }
    },
    async distributeSecutixTournamentAppTickets({ getters, dispatch }, { eventId, tickets }) {
        const seatIds = tickets.map((ticket) => ticket.seat.id);
        const ticketIds = tickets.map((ticket) => ticket.id);

        dispatch('addToProcessed', { seatIds, ticketIds });

        try {
            await ticketsDistributionApi
                .forSecutix()
                .distributeTournamentAppTickets(getters.mainEventId, eventId, {
                    tickets: ticketIds,
                });
            await dispatch('refreshTicketsList', { withLoading: false, ticketIds });
        } finally {
            dispatch('removeFromProcessed', { seatIds, ticketIds });
        }
    },
    async cancelTicketDistribution({ state, getters, dispatch }, { eventId, ticket }) {
        dispatch('addToProcessed', { seatIds: [ticket.seat.id], ticketIds: [ticket.id] });

        try {
            if (state.mainEvent.eticketing_type === ETicketingType.STANDARD) {
                await ticketsDistributionApi.cancelETicketsDistribution(
                    getters.mainEventId,
                    getters.eventId,
                    {
                        tickets: [ticket.id],
                    }
                );
            } else {
                await ticketsDistributionApi.cancelDistribution(eventId, ticket.id);
            }
        } finally {
            await dispatch('refreshTicketsList', { withLoading: false, ticketIds: [ticket.id] });
            dispatch('removeFromProcessed', { seatIds: [ticket.seat.id], ticketIds: [ticket.id] });
        }
    },
    async undoTicketDistribution({ dispatch, getters }, { eventId, ticket }) {
        dispatch('addToProcessed', { seatIds: [ticket.seat.id], ticketIds: [ticket.id] });

        try {
            if (getters.isStandardETicketingEnabled) {
                await ticketsDistributionApi.undoETicketsDistribution(
                    getters.mainEventId,
                    getters.eventId,
                    {
                        tickets: [ticket.id],
                        go_seat_plan_id: getters.currentGOSeatPlanId,
                    }
                );
            } else {
                await ticketsDistributionApi.undoDistribution(eventId, ticket.id);
            }
        } finally {
            await dispatch('refreshTicketsList', { withLoading: false, ticketIds: [ticket.id] });
            dispatch('removeFromProcessed', { seatIds: [ticket.seat.id], ticketIds: [ticket.id] });
        }
    },
    async assignSeatsInBulk({ state, getters, rootGetters, commit, dispatch }) {
        const seatIds = sortByMany(
            [
                localeComparator(
                    (seatId) => getters.seatsMap[seatId]?.row.name,
                    SortDirection.ASC,
                    {
                        numeric: true,
                        sensitivity: 'base',
                    }
                ),
                localeComparator((seatId) => getters.seatsMap[seatId]?.name, SortDirection.ASC, {
                    numeric: true,
                    sensitivity: 'base',
                }),
            ],
            state.highlightedSeatIdsWithoutAllocation
        );

        const tickets =
            rootGetters['SeatAllocation/guestOperations/TicketList/notProcessedActiveTickets'];
        const ticketIds = tickets.map((ticket) => ticket.id);
        const [alreadyDistributedTickets, notDistributedTickets] = partition(
            isTicketDistributed,
            tickets
        );

        const processedSeatIds = [
            ...seatIds,
            ...tickets.map((ticket) => ticket.seat?.id).filter(Boolean),
        ];

        if (alreadyDistributedTickets.length > 0) {
            const modalResult = await dispatchShowBackdropComponent(
                dispatch,
                {
                    component: loadAsyncComponent(
                        () =>
                            import(
                                '@/app/modules/seating/guestOperations/Picker/components/BulkAssignmentDistributedTicketsModal.vue'
                            )
                    ),
                    props: {
                        distributedTickets: alreadyDistributedTickets,
                    },
                },
                { root: true }
            );

            if (isModalCancelled(modalResult)) {
                return;
            }
        }

        dispatch('addToProcessed', {
            seatIds: processedSeatIds,
            ticketIds,
        });

        try {
            const allocations = notDistributedTickets.map((ticket, index) => ({
                ticket_id: ticket.id,
                venue_seat_id: seatIds[index],
            }));

            await guestOperationsSeatAllocationApi.allocateSeats(
                getters.eventId,
                getters.currentGOSeatPlan.id,
                allocations
            );
            await commit('setHighlightedSeatIdsWithoutAllocation', []);
        } finally {
            await dispatch('refreshTicketsList', {
                ticketIds: notDistributedTickets.map((ticket) => ticket.id),
            });

            dispatch('removeFromProcessed', {
                seatIds: processedSeatIds,
                ticketIds,
            });
        }
    },
    async distributeETickets({ dispatch }, { mainEventId, eventId, tickets }) {
        return dispatchShowBackdropComponent(
            dispatch,
            {
                component: loadAsyncComponent(
                    () =>
                        import(
                            '@/app/modules/seating/guestOperations/Picker/components/DistributeETicketsSidePanel/DistributeETicketsSidePanel.vue'
                        )
                ),
                props: {
                    mainEventId: mainEventId,
                    eventId: eventId,
                    tickets,
                },
                events: {
                    [BACKDROP_EVENTS.CONFIRMED]: async (tickets, { setSubmitting }) => {
                        const seatIds = tickets.map((ticket) => ticket.seat.id);
                        const ticketIds = tickets.map((ticket) => ticket.id);

                        setSubmitting(true);
                        try {
                            dispatch('addToProcessed', {
                                seatIds,
                                ticketIds,
                            });

                            await dispatch('refreshTicketsList', {
                                withLoading: false,
                                ticketIds: tickets.map((ticket) => ticket.id),
                                deselectTickets: true,
                            });
                        } finally {
                            dispatch('removeFromProcessed', {
                                seatIds,
                                ticketIds,
                            });
                            setSubmitting(false);
                        }
                    },
                },
            },
            { root: true }
        );
    },
    updateSeatPlan({ state, commit }, { key, value }) {
        commit(
            'setSeatPlan',
            Object.freeze({
                ...state.seatPlan,
                [key]: value,
            })
        );
    },
    addToProcessed({ commit }, { seatIds, ticketIds }) {
        if (seatIds) {
            commit('addSeatIdsToProcessed', seatIds);
        }

        if (ticketIds) {
            commit('SeatAllocation/guestOperations/TicketList/addTicketIdsToProcessed', ticketIds, {
                root: true,
            });
        }
    },
    removeFromProcessed({ commit }, { seatIds, ticketIds }) {
        if (seatIds) {
            commit('removeSeatIdsFromProcessed', seatIds);
        }

        if (ticketIds) {
            commit(
                'SeatAllocation/guestOperations/TicketList/removeTicketIdsFromProcessed',
                ticketIds,
                {
                    root: true,
                }
            );
        }
    },
};

const mutations = {
    initialize(storeState) {
        Object.assign(storeState, initialState());
    },
    setLoadingPage(state, loading) {
        state.loadingPage = loading;
    },
    setLoadingPageError(state, error) {
        state.loadingPageError = error;
    },
    setLoading(state, visible) {
        state.loading = visible;
    },
    setSeatCategories(state, seatCategories) {
        state.seatCategories = seatCategories;
    },
    setParticipantIdentifiers(state, participantIdentifiers) {
        state.participantIdentifiers = participantIdentifiers;
    },
    setGOSeatCategoriesByType(state, seatCategories) {
        state.goSeatCategoriesByType = seatCategories;
    },
    setMainEvent(state, mainEvent) {
        state.mainEvent = mainEvent;
    },
    setEvent(state, event) {
        state.event = event;
    },
    setSeatingSettings(state, seatingSettings) {
        state.seatingSettings = seatingSettings;
    },
    setSeatPlan(state, seatPlan) {
        state.seatPlan = seatPlan;
    },
    previewSeat(state, seat) {
        state.previewedSeat = seat;
    },
    setPreviewedSeatLocked(state, locked) {
        state.previewedSeat.seat.is_locked = locked;
    },
    startBatch(state, tickets) {
        state.batch = {
            enabled: true,
            tickets,
            currentIndex: null,
            currentTicketId: null,
        };
    },
    endBatch(state) {
        state.batch = initialState().batch;
    },
    updateBatchItem(state, { index, ticketId }) {
        state.batch.currentIndex = index;
        state.batch.currentTicketId = ticketId;
    },
    setCurrentGOSeatPlan(state, seatPlan) {
        state.GOSeatPlans.current = seatPlan;
    },
    setGOSeatPlans(state, seatPlans) {
        state.GOSeatPlans.available = seatPlans;
    },
    setSeatTagPlan(state, seatTagPlanId) {
        state.GOSeatPlans.available = state.GOSeatPlans.available.map((seatPlan) => ({
            ...seatPlan,
            is_seat_tag_plan: seatTagPlanId === seatPlan.id,
        }));
        state.GOSeatPlans.current = state.GOSeatPlans.available.find(
            (seatPlan) => seatPlan.id === state.GOSeatPlans.current?.id
        );
    },
    setTabletMode(state, enabled) {
        state.isTabletMode = enabled;
    },
    setHighlightedSeatIdsWithoutAllocation(state, ids) {
        state.highlightedSeatIdsWithoutAllocation = ids;
    },
    setSeatIdToPDFTemplateMap(state, seatIdToPDFTemplateMap) {
        state.seatIdToPDFTemplateMap = seatIdToPDFTemplateMap;
    },
    setProcessedSeatIds(state, ids) {
        state.processedSeatIds = ids;
    },
    addSeatIdsToProcessed(state, ids) {
        state.processedSeatIds = uniqueValues([...state.processedSeatIds, ...ids].filter(Boolean));
    },
    removeSeatIdsFromProcessed(state, ids, reRender = true) {
        state.processedSeatIds = state.processedSeatIds.filter((id) => !ids.includes(id));
        if (reRender) {
            state.seatIdsToReRender = uniqueValues([...state.seatIdsToReRender, ...ids]);
        }
    },
    addSeatIdsToReRender(state, seatIds) {
        state.seatIdsToReRender = uniqueValues([...state.seatIdsToReRender, ...seatIds]);
    },
    removeSeatIdsToReRender(state, seatIds) {
        state.seatIdsToReRender = state.seatIdsToReRender.filter(
            (seatId) => !seatIds.includes(seatId)
        );
    },
    bumpReRenderId(state) {
        state.reRenderId = state.reRenderId + 1;
    },
    useFabricJs(state, enabled) {
        state.usingFabricJs = enabled;
    },
};

const findRelatedTickets = (ticketsTree, ticket) => {
    const rootTicket = ticketsTree.find(
        (_ticket) => _ticket.guest.id === (ticket.guest.patron_id ?? ticket.guest.id)
    );
    return rootTicket
        ? [rootTicket, ...(rootTicket.nestedTickets ?? [])].filter(
              (_ticket) => _ticket.id !== ticket.id
          )
        : [];
};

export default {
    namespaced: true,
    state: initialState,
    getters,
    actions,
    mutations,
};
